import React from 'react';
import { EVENT_TYPE_META, iso, monthGrid, startOfWeek, addDays, eventsOnDate } from '@/constants/calendarMeta';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const Chip = ({ ev, onClick }) => {
  const meta = EVENT_TYPE_META[ev.type] || EVENT_TYPE_META.other;
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(ev); }}
      className={`w-full text-left truncate text-[10px] px-1.5 py-0.5 rounded border ${meta.bg} ${meta.text} ${meta.border} hover:opacity-80`}
      title={`${ev.start_time ? ev.start_time + ' · ' : ''}${ev.title}`}
      data-testid={`cal-chip-${ev.id}`}>
      {ev.start_time ? `${ev.start_time} ` : ''}{ev.title}
    </button>
  );
};

export const MonthView = ({ year, month, events, today, selected, onSelectDate, onOpenEvent }) => {
  const cells = monthGrid(year, month);
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] overflow-hidden" data-testid="calendar-month">
      <div className="grid grid-cols-7 bg-[hsl(var(--surface-elevated))] text-[11px] uppercase tracking-wide text-[hsl(var(--text-muted))]">
        {DOW.map((d) => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d) => {
          const key = iso(d);
          const dayEvents = eventsOnDate(events, key);
          const muted = d.getMonth() !== month;
          return (
            <div key={key} role="button" tabIndex={0}
              onClick={() => onSelectDate(key)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectDate(key); } }}
              className={`min-h-[86px] text-left border-t border-l border-[hsl(var(--border))] p-1.5 transition-colors
                ${muted ? 'bg-[hsl(var(--surface))]/40' : 'bg-[hsl(var(--surface))]'}
                ${selected === key ? 'ring-1 ring-inset ring-[hsl(var(--primary))]' : 'hover:bg-white/5'}`}
              data-testid={`cal-day-${key}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs ${key === today ? 'px-1.5 rounded-full bg-[hsl(var(--primary))] text-white' : muted ? 'text-[hsl(var(--text-muted))]/60' : 'text-[hsl(var(--text-muted))]'}`}>
                  {d.getDate()}
                </span>
                {dayEvents.length > 2 && (
                  <span className="text-[9px] text-[hsl(var(--text-muted))]">+{dayEvents.length - 2}</span>
                )}
              </div>
              <div className="space-y-1">
                {dayEvents.slice(0, 2).map((ev) => <Chip key={ev.id} ev={ev} onClick={onOpenEvent} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const WeekView = ({ anchorDate, events, today, selected, onSelectDate, onOpenEvent }) => {
  const start = startOfWeek(anchorDate);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="calendar-week">
      {days.map((d) => {
        const key = iso(d);
        const dayEvents = eventsOnDate(events, key);
        return (
          <div key={key} role="button" tabIndex={0}
            onClick={() => onSelectDate(key)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectDate(key); } }}
            className={`text-left rounded-xl border p-2.5 min-h-[140px] transition-colors
              ${selected === key ? 'border-[hsl(var(--primary))]' : 'border-[hsl(var(--border))] hover:bg-white/5'}
              bg-[hsl(var(--surface))]`}
            data-testid={`cal-week-day-${key}`}>
            <p className="text-[11px] uppercase tracking-wide text-[hsl(var(--text-muted))]">{DOW[(d.getDay() + 6) % 7]}</p>
            <p className={`text-sm font-semibold mb-2 ${key === today ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--text-primary))]'}`}>
              {d.getDate()}/{d.getMonth() + 1}
            </p>
            <div className="space-y-1">
              {dayEvents.length === 0 && <p className="text-[10px] text-[hsl(var(--text-muted))]">—</p>}
              {dayEvents.map((ev) => <Chip key={ev.id} ev={ev} onClick={onOpenEvent} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
};
